const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: { field: string; message: string }[],
    /** Raw error envelope, for structured refusals. */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The envelope's string `code` (e.g. `PLAN_FEATURE_LOCKED`), when the API sent one. */
  get code(): string | undefined {
    return typeof this.body?.code === 'string' ? this.body.code : undefined;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: requestHeaders,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  // On 401, try refreshing the token once
  if (res.status === 401 && !skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: requestHeaders,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
    } else {
      localStorage.removeItem('bbpos_kds_user');
      window.location.href = '/login';
      throw new ApiError(401, 'Session expired. Please log in again.');
    }
  }

  const json = await res.json().catch(() => null);

  if (!res.ok) {
    const message =
      json?.message ??
      json?.error ??
      `Request failed with status ${res.status}`;
    const errorMessage = Array.isArray(message) ? message.join(', ') : message;
    throw new ApiError(res.status, errorMessage, json?.details, json ?? undefined);
  }

  if (json.meta !== undefined) {
    return { data: json.data, meta: json.meta } as T;
  }
  return json.data as T;
}
